/**
 * Scorekeeper state: games played with physical cards.
 *
 * Everything lives in localStorage, so the whole mode works with no server and
 * no connection. Each game is one self-contained document keyed by a
 * client-generated uuid, which is also the key the sync endpoint upserts on.
 */

import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type {
  GameConfig,
  HouseRules,
  PlayerProfile,
  RoundDirection,
  ScoreGame,
  ScoreRound,
  ScoringConfig,
  ScoringPresetId,
  Seat,
} from '@shared/types/game';
import { DEFAULT_HOUSE_RULES } from '@shared/types/game';
import {
  DEFAULT_MAX_CARDS_CAP,
  DEFAULT_SCORING_PRESET,
  SCORING_PRESETS,
  buildRoundPlan,
  scoreRound,
  scoringConfigFor,
} from '@shared/game-logic';
import { newId } from '@/lib/id';
import { PLAYER_EMOJI } from '@/lib/labels';

export interface NewGameInput {
  seats: Array<{ displayName: string; profileId: string | null }>;
  maxCards: number;
  direction: RoundDirection;
  repeatTopRound: boolean;
  extraNoTrumpRound: boolean;
  noTrumpOnTheWayDown: boolean;
  firstDealerSeat: number;
  scoringPreset: ScoringPresetId;
  scoring: ScoringConfig;
  houseRules: HouseRules;
}

export interface ScorekeeperData {
  games: Record<string, ScoreGame>;
  profiles: PlayerProfile[];
  /** Last game opened, so the home screen can offer "verder spelen". */
  lastGameId: string | null;
  /** Games whose latest snapshot has not reached the server yet. */
  pendingSync: string[];
}

interface ScorekeeperActions {
  createGame: (input: NewGameInput) => string;
  deleteGame: (gameId: string) => void;
  setBid: (gameId: string, roundNo: number, seat: number, bid: number | null) => void;
  setTricks: (gameId: string, roundNo: number, seat: number, tricksWon: number | null) => void;
  setTrump: (gameId: string, roundNo: number, trump: ScoreRound['trump']) => void;
  /** Bids are in: move the round on to entering tricks. */
  confirmBids: (gameId: string, roundNo: number) => void;
  /** Back to bidding to fix a mistyped bid, keeping what was entered. */
  backToBidding: (gameId: string, roundNo: number) => void;
  /** Tricks are in: score the round and move on. */
  confirmRound: (gameId: string, roundNo: number) => void;
  /** Reopen an already-scored round for correction; totals recompute. */
  reopenRound: (gameId: string, roundNo: number) => void;
  addProfile: (name: string, emoji?: string) => PlayerProfile;
  renameProfile: (profileId: string, name: string) => void;
  deleteProfile: (profileId: string) => void;
  markSynced: (gameId: string) => void;
  /**
   * Put a potje from the server onto this device.
   *
   * Not queued for sync: it came from the server, so pushing it straight back
   * would be a round trip that can only lose. The first real change queues it.
   */
  adoptGame: (game: ScoreGame) => void;
  touch: (gameId: string) => void;
}

export type ScorekeeperStore = ScorekeeperData & ScorekeeperActions;

/**
 * The shape actually found in localStorage, which may predate fields the current
 * types require. Only the persist migration should use this.
 */
interface StoredScorekeeperData extends Omit<ScorekeeperData, 'games'> {
  games: Record<
    string,
    Omit<ScoreGame, 'config'> & {
      config: {
        roundPlan: Partial<GameConfig['roundPlan']> & { playerCount: number };
        scoringPreset: ScoringPresetId;
        scoring: Partial<ScoringConfig> & { exactBase: number };
        houseRules: Partial<HouseRules>;
      };
    }
  >;
}

export const DEFAULT_NEW_GAME: Omit<NewGameInput, 'seats'> = {
  maxCards: DEFAULT_MAX_CARDS_CAP,
  direction: 'up-down',
  repeatTopRound: false,
  extraNoTrumpRound: true,
  noTrumpOnTheWayDown: false,
  firstDealerSeat: 0,
  scoringPreset: DEFAULT_SCORING_PRESET,
  scoring: { ...SCORING_PRESETS['nl-standaard'] },
  houseRules: { ...DEFAULT_HOUSE_RULES },
};

function emptyRounds(config: GameConfig): ScoreRound[] {
  return buildRoundPlan(config.roundPlan).map((planned) => ({
    roundNo: planned.roundNo,
    cards: planned.cards,
    dealerSeat: planned.dealerSeat,
    // A deliberately trumpless round starts with its trump already settled, so
    // nobody has to remember to pick "troefloos" at the table.
    trump: planned.noTrump ? null : undefined,
    status: 'bidding',
    entries: Array.from({ length: config.roundPlan.playerCount }, (_, seat) => ({
      seat,
      bid: null,
      tricksWon: null,
      points: null,
    })),
  }));
}

/**
 * Apply a change to one game, refreshing `updatedAt`, recomputing whether the
 * game is finished, and queueing it for sync. Every mutation goes through here
 * so none of that can be forgotten.
 */
function updateGame(
  state: ScorekeeperData,
  gameId: string,
  change: (game: ScoreGame) => ScoreGame
): Partial<ScorekeeperData> {
  const existing = state.games[gameId];
  if (!existing) return {};

  const changed = change(existing);
  const allDone = changed.rounds.every((round) => round.status === 'done');

  const next: ScoreGame = {
    ...changed,
    updatedAt: new Date().toISOString(),
    status: allDone ? 'finished' : 'in-progress',
    finishedAt: allDone ? (changed.finishedAt ?? new Date().toISOString()) : null,
  };

  return {
    games: { ...state.games, [gameId]: next },
    pendingSync: state.pendingSync.includes(gameId)
      ? state.pendingSync
      : [...state.pendingSync, gameId],
  };
}

function mapRound(
  game: ScoreGame,
  roundNo: number,
  change: (round: ScoreRound) => ScoreRound
): ScoreGame {
  return {
    ...game,
    rounds: game.rounds.map((round) => (round.roundNo === roundNo ? change(round) : round)),
  };
}

export const useScorekeeper = create<ScorekeeperStore>()(
  persist(
    (set, get) => ({
      games: {},
      profiles: [],
      lastGameId: null,
      pendingSync: [],

      createGame: (input) => {
        const id = newId();
        const now = new Date().toISOString();

        const seats: Seat[] = input.seats.map((seat, index) => ({
          seat: index,
          displayName: seat.displayName.trim() || `Speler ${index + 1}`,
          profileId: seat.profileId,
        }));

        const config: GameConfig = {
          roundPlan: {
            playerCount: seats.length,
            maxCards: input.maxCards,
            direction: input.direction,
            repeatTopRound: input.repeatTopRound,
            extraNoTrumpRound: input.extraNoTrumpRound,
            noTrumpOnTheWayDown: input.noTrumpOnTheWayDown,
            firstDealerSeat: input.firstDealerSeat,
          },
          scoringPreset: input.scoringPreset,
          scoring:
            input.scoringPreset === 'eigen'
              ? { ...input.scoring }
              : scoringConfigFor(input.scoringPreset),
          houseRules: { ...input.houseRules },
        };

        const game: ScoreGame = {
          id,
          mode: 'scorekeeper',
          createdAt: now,
          updatedAt: now,
          finishedAt: null,
          status: 'in-progress',
          config,
          seats,
          rounds: emptyRounds(config),
        };

        set((state) => ({
          games: { ...state.games, [id]: game },
          lastGameId: id,
          pendingSync: [...state.pendingSync, id],
        }));

        return id;
      },

      deleteGame: (gameId) =>
        set((state) => {
          const games = { ...state.games };
          delete games[gameId];
          return {
            games,
            lastGameId: state.lastGameId === gameId ? null : state.lastGameId,
            pendingSync: state.pendingSync.filter((id) => id !== gameId),
          };
        }),

      setBid: (gameId, roundNo, seat, bid) =>
        set((state) =>
          updateGame(state, gameId, (game) =>
            mapRound(game, roundNo, (round) => ({
              ...round,
              entries: round.entries.map((entry) =>
                entry.seat === seat ? { ...entry, bid } : entry
              ),
            }))
          )
        ),

      setTricks: (gameId, roundNo, seat, tricksWon) =>
        set((state) =>
          updateGame(state, gameId, (game) =>
            mapRound(game, roundNo, (round) => ({
              ...round,
              entries: round.entries.map((entry) =>
                entry.seat === seat ? { ...entry, tricksWon } : entry
              ),
            }))
          )
        ),

      setTrump: (gameId, roundNo, trump) =>
        set((state) =>
          updateGame(state, gameId, (game) =>
            mapRound(game, roundNo, (round) => ({ ...round, trump }))
          )
        ),

      confirmBids: (gameId, roundNo) =>
        set((state) =>
          updateGame(state, gameId, (game) =>
            mapRound(game, roundNo, (round) => ({ ...round, status: 'playing' }))
          )
        ),

      backToBidding: (gameId, roundNo) =>
        set((state) =>
          updateGame(state, gameId, (game) =>
            mapRound(game, roundNo, (round) => ({ ...round, status: 'bidding' }))
          )
        ),

      confirmRound: (gameId, roundNo) =>
        set((state) => {
          const game = state.games[gameId];
          if (!game) return {};
          const config = game.config.scoring;

          return updateGame(state, gameId, (current) =>
            mapRound(current, roundNo, (round) => ({
              ...round,
              status: 'done',
              entries: round.entries.map((entry) => ({
                ...entry,
                points:
                  entry.bid === null || entry.tricksWon === null
                    ? null
                    : scoreRound(entry.bid, entry.tricksWon, config),
              })),
            }))
          );
        }),

      reopenRound: (gameId, roundNo) =>
        set((state) =>
          updateGame(state, gameId, (game) => ({
            ...mapRound(game, roundNo, (round) => ({
              ...round,
              status: 'bidding',
              entries: round.entries.map((entry) => ({ ...entry, points: null })),
            })),
            // A reopened round means the game is no longer finished.
            finishedAt: null,
          }))
        ),

      addProfile: (name, emoji) => {
        const profile: PlayerProfile = {
          id: newId(),
          name: name.trim(),
          emoji: emoji ?? PLAYER_EMOJI[get().profiles.length % PLAYER_EMOJI.length],
        };
        set((state) => ({ profiles: [...state.profiles, profile] }));
        return profile;
      },

      renameProfile: (profileId, name) =>
        set((state) => ({
          profiles: state.profiles.map((profile) =>
            profile.id === profileId ? { ...profile, name: name.trim() } : profile
          ),
        })),

      deleteProfile: (profileId) =>
        set((state) => ({
          profiles: state.profiles.filter((profile) => profile.id !== profileId),
        })),

      markSynced: (gameId) =>
        set((state) => ({ pendingSync: state.pendingSync.filter((id) => id !== gameId) })),

      adoptGame: (game) =>
        set((state) => ({
          games: { ...state.games, [game.id]: game },
          lastGameId: game.id,
        })),

      touch: (gameId) => set({ lastGameId: gameId }),
    }),
    {
      name: 'boerenbridge.scorekeeper',
      storage: createJSONStorage(() => localStorage),
      version: 2,
      /**
       * Fill in fields that games stored by an older version do not have.
       *
       * Without this, reopening a round in an old game would compute its points
       * against `bidSquaredBonus: undefined` and quietly write `NaN` into the
       * scorecard. The values chosen here are the ones that keep every existing
       * score exactly as it was: no squared bonus, no extra no-trump round.
       */
      migrate: (persisted, fromVersion) => {
        // Deliberately typed loose: what is on disk was written by an older
        // version and genuinely lacks these fields, which the current types
        // declare as present. Pretending otherwise is how the NaN slips through.
        const state = persisted as StoredScorekeeperData | undefined;
        if (!state?.games || fromVersion >= 2) return state as ScorekeeperData;

        return {
          ...state,
          games: Object.fromEntries(
            Object.entries(state.games).map(([id, game]) => [
              id,
              {
                ...game,
                config: {
                  ...game.config,
                  scoring: { bidSquaredBonus: 0, ...game.config.scoring },
                  roundPlan: {
                    extraNoTrumpRound: false,
                    noTrumpOnTheWayDown: false,
                    ...game.config.roundPlan,
                  },
                  houseRules: { blindSingleCardRound: true, ...game.config.houseRules },
                },
              },
            ])
          ),
        } as ScorekeeperData;
      },
    }
  )
);

// --- selectors -------------------------------------------------------------

export function selectGame(gameId: string | undefined) {
  return (state: ScorekeeperStore): ScoreGame | undefined =>
    gameId ? state.games[gameId] : undefined;
}

/** Games newest first, for the home screen and the history list. */
export function selectGameList(state: ScorekeeperStore): ScoreGame[] {
  return Object.values(state.games).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

/**
 * The round the table is on: the first that is not scored yet. Reopening an
 * earlier round therefore pulls the app back to it, which is what you want when
 * correcting a mistake mid-game.
 */
export function currentRoundOf(game: ScoreGame): ScoreRound | undefined {
  return game.rounds.find((round) => round.status !== 'done');
}
